/**
 * Create service ticket — OPERATOR / SUPER_ADMIN.
 * Station lookup via stationByNumber (single object); partner/assignee auto-filled.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import * as api from '../../modules/api';
import PageContainer from '../mui-crud/components/PageContainer';
import { CopyableFieldWithAction } from '../components/DetailDrawerContent';
import useNotifications from '../mui-crud/hooks/useNotifications/useNotifications';
import { composeOtherDescription, defaultTaskDescription } from './serviceTicketLabels';

const TYPES = ['OFFLINE', 'EMPTY', 'FULL', 'OTHER'];

function typeLabel(t, type) {
  const map = {
    OFFLINE: t('service.typeOffline'),
    EMPTY: t('service.typeEmpty'),
    FULL: t('service.typeFull'),
    OTHER: t('service.typeOther'),
  };
  return map[type] || type;
}

/** Normalize stationByNumber / FindStationByID / list payloads into option objects. */
function stationsFromResponse(data) {
  if (data == null) return [];
  const list = Array.isArray(data) ? data : [data];
  return list
    .map((raw) => {
      const s = raw?.station || raw || {};
      const stationNumber = (s.stationNumber || raw?.stationNumber || '').toString().trim();
      if (!stationNumber) return null;
      const owner = (s.owner || raw?.owner || '').toString().trim();
      const address = (
        s.additionalAddress || s.address || raw?.additionalAddress || raw?.address || ''
      ).toString().trim();
      return {
        stationNumber,
        owner,
        address,
        label: `${stationNumber}${address ? ` — ${address}` : ''}`,
      };
    })
    .filter(Boolean);
}

export default function ServiceTicketCreatePage() {
  const { t } = useTranslation();
  const history = useHistory();
  const notifications = useNotifications();
  const [type, setType] = useState('OFFLINE');
  const [station, setStation] = useState(null);
  const [stationInput, setStationInput] = useState('');
  const [stationOptions, setStationOptions] = useState([]);
  const [stationLoading, setStationLoading] = useState(false);
  const [stationHint, setStationHint] = useState('');
  const [partnerEmail, setPartnerEmail] = useState('');
  const [taskTitle, setTaskTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assigneeEmail, setAssigneeEmail] = useState('');
  const [technicians, setTechnicians] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadTechnicians = useCallback(async (parentAdminEmail) => {
    if (!parentAdminEmail) {
      setTechnicians([]);
      setAssigneeEmail('');
      return;
    }
    try {
      const { data } = await api.getServiceTechnicians({
        parentAdminEmail,
        active: true,
      });
      const list = Array.isArray(data) ? data.filter((p) => p.active !== false) : [];
      setTechnicians(list);
      if (list.length === 1) {
        setAssigneeEmail(list[0].email);
      } else if (list.length === 0) {
        // No technicians in network — leave unassigned (CREATED). Do not force merchant email.
        setAssigneeEmail('');
      } else {
        setAssigneeEmail((prev) => (list.some((x) => x.email === prev) ? prev : list[0].email));
      }
    } catch {
      setTechnicians([]);
      setAssigneeEmail(parentAdminEmail);
    }
  }, []);

  const resolvePartnerAndTechs = useCallback(async (stationOption) => {
    if (!stationOption) {
      setPartnerEmail('');
      setTechnicians([]);
      setAssigneeEmail('');
      return;
    }
    let networkAdmin = stationOption.owner || '';
    try {
      const { data } = await api.getMerchants(true);
      const list = Array.isArray(data) ? data : [];
      const merchant = list.find(
        (m) => (m.email || '').toLowerCase() === (stationOption.owner || '').toLowerCase(),
      );
      if (merchant?.partnerEmail) {
        networkAdmin = merchant.partnerEmail;
      } else if (!networkAdmin) {
        // owner might already be partner ADMIN
        networkAdmin = stationOption.owner || '';
      }
    } catch {
      // keep owner
    }
    setPartnerEmail(networkAdmin);
    await loadTechnicians(networkAdmin);
  }, [loadTechnicians]);

  const lookupStation = useCallback(async (query) => {
    // Autocomplete may pass the selected option label ("8130 — Address") — use digits only.
    const raw = (query || '').trim();
    const q = (raw.match(/^\d+/) || [raw.replace(/\D/g, '')])[0] || '';
    if (q.length < 4) {
      setStationOptions([]);
      setStationHint(t('service.stationSearch'));
      return;
    }
    setStationLoading(true);
    setStationHint('');
    try {
      const { data } = await api.findStationByNumber(q);
      const options = stationsFromResponse(data);
      setStationOptions(options);
      if (options.length === 0) {
        setStationHint(t('service.stationNotFound'));
        return;
      }
      // Auto-select exact match
      const exact = options.find((o) => o.stationNumber === q);
      if (exact) {
        setStation(exact);
        setStationHint('');
        await resolvePartnerAndTechs(exact);
      }
    } catch {
      setStationOptions([]);
      setStationHint(t('service.stationNotFound'));
    } finally {
      setStationLoading(false);
    }
  }, [t, resolvePartnerAndTechs]);

  useEffect(() => {
    const digits = ((stationInput || '').match(/^\d+/) || [''])[0];
    if (station?.stationNumber && digits === station.stationNumber) {
      setStationHint('');
      return undefined;
    }
    const timer = setTimeout(() => lookupStation(stationInput), 350);
    return () => clearTimeout(timer);
  }, [stationInput, lookupStation, station]);

  const onStationChange = async (_e, value) => {
    setStation(value);
    if (!value) {
      setPartnerEmail('');
      setTechnicians([]);
      setAssigneeEmail('');
      return;
    }
    setStationHint('');
    await resolvePartnerAndTechs(value);
  };

  const assigneeChoices = technicians.map((tech) => ({
    email: tech.email,
    label: tech.name ? `${tech.name} (${tech.email})` : tech.email,
  }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (!station?.stationNumber) {
        setError(t('service.stationRequired'));
        setSaving(false);
        return;
      }
      if (type === 'OTHER' && !taskTitle.trim()) {
        setError(t('service.taskTitleHint'));
        setSaving(false);
        return;
      }
      const body = {
        type,
        stationNumber: station.stationNumber,
        description: type === 'OTHER'
          ? composeOtherDescription(taskTitle, description)
          : defaultTaskDescription(type),
        assigneeEmail: assigneeEmail || undefined,
      };
      const { data } = await api.createServiceTicket(body);
      history.replace(`/service/tickets/${encodeURIComponent(data.id)}`);
    } catch (err) {
      setError(
        err?.response?.data?.detail
          || err?.response?.data?.message
          || err?.message
          || t('service.errorSave'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer
      title={t('service.create')}
      breadcrumbs={[
        { title: t('service.ticketsTitle'), path: '/service/tickets' },
        { title: t('service.create') },
      ]}
    >
      <Stack component="form" onSubmit={onSubmit} spacing={2.5} sx={{ maxWidth: 640, width: '100%' }}>
        {error && <Alert severity="error">{error}</Alert>}

        <TextField
          select
          label={t('service.colType')}
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            setTaskTitle('');
            setDescription('');
          }}
          required
          fullWidth
        >
          {TYPES.map((tp) => (
            <MenuItem key={tp} value={tp}>{typeLabel(t, tp)}</MenuItem>
          ))}
        </TextField>

        <Autocomplete
          options={stationOptions}
          value={station}
          inputValue={stationInput}
          onInputChange={(_e, v, reason) => {
            if (reason === 'reset') {
              setStationInput(v);
              return;
            }
            setStationInput(v);
            if (!v) {
              setStation(null);
              setPartnerEmail('');
              setAssigneeEmail('');
              setTechnicians([]);
            }
          }}
          onChange={onStationChange}
          getOptionLabel={(o) => o?.label || o?.stationNumber || ''}
          isOptionEqualToValue={(a, b) => a?.stationNumber === b?.stationNumber}
          filterOptions={(x) => x}
          loading={stationLoading}
          noOptionsText={stationHint || t('service.stationSearch')}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('service.colStation')}
              helperText={station ? undefined : (stationHint || t('service.stationSearch'))}
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {stationLoading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <CopyableFieldWithAction
          label={t('service.partner')}
          value={partnerEmail || ''}
          onCopy={partnerEmail ? (v) => {
            navigator.clipboard.writeText(String(v)).then(
              () => notifications?.show?.(t('drawer.copySuccess') || t('drawer.copy'), { severity: 'success', autoHideDuration: 2000 }),
              () => {},
            );
          } : undefined}
          copyTooltip={t('drawer.copy')}
          onOpen={partnerEmail ? () => {
            try { sessionStorage.setItem('crossLinkMerchant', String(partnerEmail)); } catch (_) { /* ignore */ }
            window.location.hash = '#/merchants';
          } : undefined}
          openTooltip={t('service.openInMerchants')}
        />
        <Typography variant="caption" color="text.secondary">{t('service.partnerAutoHint')}</Typography>

        {assigneeChoices.length > 1 ? (
          <TextField
            select
            label={t('service.colAssignee')}
            value={assigneeEmail}
            onChange={(e) => setAssigneeEmail(e.target.value)}
            fullWidth
            helperText={t('service.assigneeMultiHint')}
          >
            {assigneeChoices.map((c) => (
              <MenuItem key={c.email} value={c.email}>{c.label}</MenuItem>
            ))}
          </TextField>
        ) : (
          <Stack spacing={0.5}>
            <Typography variant="caption" color="text.secondary">{t('service.colAssignee')}</Typography>
            <Typography variant="body1">{assigneeEmail || '—'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {assigneeEmail
                ? t('service.assigneeAutoHint')
                : (partnerEmail ? t('service.assigneeNoneHint') : t('service.assigneeAutoHint'))}
            </Typography>
          </Stack>
        )}

        {type === 'OTHER' && (
          <>
            <TextField
              label={t('service.taskTitle')}
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              helperText={t('service.taskTitleHint')}
              required
              fullWidth
            />
            <TextField
              label={t('service.description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              minRows={4}
              maxRows={16}
              fullWidth
              sx={{
                width: '100%',
                '& .MuiInputBase-root': {
                  alignItems: 'flex-start',
                  overflow: 'hidden',
                },
                '& .MuiInputBase-inputMultiline': {
                  width: '100% !important',
                  boxSizing: 'border-box',
                  overflow: 'auto !important',
                  resize: 'vertical',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                },
              }}
            />
          </>
        )}

        <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mt: 1 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push('/service/tickets')}
          >
            {t('service.back')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={saving || !station?.stationNumber || (type === 'OTHER' && !taskTitle.trim())}
            sx={{ minWidth: 160, color: 'primary.contrastText' }}
          >
            {t('service.create')}
          </Button>
        </Stack>
      </Stack>
    </PageContainer>
  );
}
